import React, { useCallback, useEffect, useRef, ReactNode, HTMLAttributes } from 'react';
import clsx from 'clsx';

import Portal from '@smart-tv/components/portal/portal';
import useDialog from '@smart-tv/hooks/use-dialog';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import useLockedBody from '@smart-tv/hooks/use-locked-body';
import styles from './overlay.module.scss';
import { isBackKey } from '@smart-tv/utils/remoteKeys';

export interface Props extends HTMLAttributes<HTMLDivElement> {
  open: boolean;
  children: ReactNode | ReactNode[];
  onClose: () => void;
  onBackdropClick?: () => void;
  hideBackdrop?: boolean;
  disableEscapeKeyDown?: boolean;
  overlayContainerProps?: any;
  disableLockBodyScroll?: boolean;
  disablePortal?: boolean;
}

const OverlayInner = (props: Props) => {
  const {
    children,
    onClose,
    onBackdropClick,
    hideBackdrop = false,
    disableLockBodyScroll = false,
    disableEscapeKeyDown = false,
    overlayContainerProps,
    ...rest
  } = props;

  const overlayRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (isBackKey(event) && !disableEscapeKeyDown) {
        event.stopPropagation();
        onClose();
      }
    },
    [disableEscapeKeyDown, onClose]
  );

  useEffect(() => {
    overlayRef.current?.focus();
  }, [overlayRef]);

  useLockedBody(!disableLockBodyScroll);

  useEventListener('keydown', (event) => handleKeyDown(event as KeyboardEvent));

  return (
    <div className={styles['overlay']} role="presentation" {...rest}>
      <div
        className={clsx(
          styles['overlay__container'],
          hideBackdrop && styles['overlay__container--hide-backdrop']
        )}
        tabIndex={0}
        role="dialog"
        ref={overlayRef}
        aria-modal={true}
        data-testid={overlayContainerProps?.['data-testid']}
        onClick={onBackdropClick}
      >
        {children}
      </div>
    </div>
  );
};

export const Overlay = (props: Props) => {
  const { open, disablePortal = false } = props;

  const { handleOpen, handleClose } = useDialog();
  useEffect(() => {
    handleOpen?.();

    return () => {
      handleClose?.();
    };
  }, [open, handleOpen, handleClose]);

  if (!open) return null;

  if (disablePortal) {
    return <OverlayInner {...props} />;
  }

  return (
    <Portal>
      <OverlayInner {...props} />
    </Portal>
  );
};

export default Overlay;
